/**
 * Yoink Adventures V2 - Location Service
 *
 * Handles location-related operations including:
 * - Reverse geocoding to extract county/state/country
 * - Lazy backfill of missing geo data
 * - Location stats aggregation
 */

import * as gateway from './supabase-gateway.js';
import { TABLES } from './database-tables.js';
import { foldRegionName, isUnitedStates } from './utils.js';
import * as logger from './logger.js';

// =============================================================================
// CONSTANTS
// =============================================================================

// The shell says where the PHP API lives via YOINK_CONFIG.apiBase
// (production theme path vs the subdomain's /adventure/api wrappers).
// Fallbacks for shells that predate apiBase: the v2 flag alone means the
// staging subdomain; otherwise the V1 theme path.
const YOINK_CONFIG = (typeof window !== 'undefined' && window.YOINK_CONFIG) || {};
const PROXY_BASE = YOINK_CONFIG.apiBase
  ? `${YOINK_CONFIG.apiBase}/google-proxy.php`
  : (YOINK_CONFIG.v2
    ? '/adventure/api/google-proxy.php'
    : '/wp-content/themes/astra/api/google-proxy.php');
const GEOCODE_ENDPOINT = `${PROXY_BASE}?action=geocode`;
const PLACE_DETAILS_ENDPOINT = `${PROXY_BASE}?action=place-details`;
const GEOCODE_TIMEOUT_MS = 10000;
const MAX_BACKFILL_BATCH = 5; // Limit concurrent geocode requests

// Normalize country names (Google returns inconsistent values like "USA" vs "United States")
const COUNTRY_NAME_MAP = {
  'US': 'United States',
  'GB': 'United Kingdom',
  'UK': 'United Kingdom',
  'BS': 'Bahamas' // Google's long_name is "The Bahamas"; store the common form
};

// =============================================================================
// GEOCODING
// =============================================================================

/**
 * Reverse geocode a lat/lng to get address components
 * @param {number} lat - Latitude
 * @param {number} lng - Longitude
 * @returns {Promise<{county: string|null, state_province: string|null, country: string|null, country_code: string|null}>}
 */
export async function reverseGeocode(lat, lng) {
  try {
    // Simple fetch like location-panel.js uses (no extra headers)
    const url = `${GEOCODE_ENDPOINT}&lat=${lat}&lng=${lng}`;
    const response = await fetch(url);

    // Always try to read the response body for error details
    const data = await response.json().catch(() => null);

    if (!response.ok) {
      const errorMsg = data?.error || data?.message || `HTTP ${response.status}`;
      return { county: null, state_province: null, country: null, country_code: null, _error: errorMsg };
    }

    // Check for API error response
    if (data?.error) {
      return { county: null, state_province: null, country: null, country_code: null, _error: data.error };
    }

    // Check Google status
    if (data?.status && data.status !== 'OK') {
      return { county: null, state_province: null, country: null, country_code: null, _error: `Google: ${data.status}` };
    }

    const result = extractGeoComponents(data);
    return result;
  } catch (err) {
    return { county: null, state_province: null, country: null, country_code: null, _error: err.message };
  }
}

/**
 * Extract county/state/country from Google Geocoding API response
 * @param {object} geocodeResponse - Response from Google Geocoding API
 * @returns {{county: string|null, state_province: string|null, country: string|null, country_code: string|null}}
 */
function extractGeoComponents(geocodeResponse) {
  const result = {
    county: null,
    state_province: null,
    country: null,
    country_code: null
  };

  if (!geocodeResponse?.results?.length) {
    return result;
  }

  // Scan ALL results, not just the first. Google's reverse-geocode response is sorted
  // most-specific first, but the most-specific match can be a plus_code, premise, or
  // establishment (e.g. inside a national park) whose address_components omit
  // administrative_area_level_1. Subsequent results almost always include it. Take the
  // first non-null value found for each field.
  let localityFallback = null;
  for (const geocodeResult of geocodeResponse.results) {
    const addressComponents = geocodeResult.address_components || [];

    for (const component of addressComponents) {
      const types = component.types || [];

      if (!result.county && types.includes('administrative_area_level_2')) {
        result.county = component.long_name;
      }

      // Capture locality as a fallback for "county" — handles US independent cities
      // (St. Louis, Baltimore, Carson City, most Virginia cities) which sit at the
      // county-equivalent level but are tagged 'locality' rather than admin_area_level_2.
      if (!localityFallback && types.includes('locality')) {
        localityFallback = component.long_name;
      }

      if (!result.state_province && types.includes('administrative_area_level_1')) {
        result.state_province = component.long_name;
      }

      if (!result.country && types.includes('country')) {
        result.country_code = component.short_name;
        result.country = COUNTRY_NAME_MAP[component.short_name] || component.long_name;
      }
    }

    if (result.county && result.state_province && result.country) break;
  }

  if (!result.county && localityFallback) {
    result.county = localityFallback;
  }

  return result;
}

/**
 * Extract county/state/country from a Places API (New) v1 Place Details response.
 * The v1 API uses a different shape: { addressComponents: [{ longText, shortText, types }] }
 * versus the Geocoding API's { results: [{ address_components: [{ long_name, short_name, types }] }] }.
 * @param {object} placeDetails - Place Details response from /place-details proxy
 * @returns {{county, state_province, country, country_code}}
 */
export function extractGeoFromPlaceDetails(placeDetails) {
  const result = {
    county: null,
    state_province: null,
    country: null,
    country_code: null
  };

  const components = placeDetails?.addressComponents || [];
  let localityFallback = null;
  for (const component of components) {
    const types = component.types || [];
    const longText = component.longText;
    const shortText = component.shortText;

    if (!result.county && types.includes('administrative_area_level_2')) {
      result.county = longText;
    }
    // Locality fallback for US independent cities (St. Louis, Baltimore, etc.)
    // which have no admin_area_level_2.
    if (!localityFallback && types.includes('locality')) {
      localityFallback = longText;
    }
    if (!result.state_province && types.includes('administrative_area_level_1')) {
      result.state_province = longText;
    }
    if (!result.country && types.includes('country')) {
      result.country_code = shortText;
      result.country = COUNTRY_NAME_MAP[shortText] || longText;
    }
  }

  if (!result.county && localityFallback) {
    result.county = localityFallback;
  }

  return result;
}

/**
 * Fetch geo components for a Google Place by place_id.
 * Uses Places API v1 addressComponents, which is more reliable than reverse-geocoding
 * for places like national parks or famous monuments where lat/lng-based reverse-geocode
 * returns only establishment/POI results without administrative_area levels.
 * @param {string} placeId
 * @returns {Promise<{county, state_province, country, country_code}|null>}
 */
async function fetchPlaceGeo(placeId) {
  if (!placeId) return null;
  try {
    const url = `${PLACE_DETAILS_ENDPOINT}&placeId=${encodeURIComponent(placeId)}&fields=addressComponents`;
    const response = await fetch(url);
    const data = await response.json().catch(() => null);
    if (!response.ok || !data || data.error) return null;
    return extractGeoFromPlaceDetails(data);
  } catch {
    return null;
  }
}

/**
 * Get geo components for a location, using whichever source works best.
 * Strategy:
 *   1. If placeId is present, try Place Details first — most reliable for parks/monuments.
 *   2. Reverse-geocode lat/lng to fill in any fields still missing.
 * @param {{placeId?: string|null, lat?: number, lng?: number}} input
 * @returns {Promise<{county, state_province, country, country_code}>}
 */
export async function getLocationGeo({ placeId, lat, lng }) {
  let result = { county: null, state_province: null, country: null, country_code: null };

  if (placeId) {
    const placeGeo = await fetchPlaceGeo(placeId);
    if (placeGeo) {
      result = placeGeo;
      if (result.county && result.state_province && result.country) {
        return result;
      }
    }
  }

  if (lat != null && lng != null) {
    const geocodeGeo = await reverseGeocode(lat, lng);
    if (!result.county && geocodeGeo.county) result.county = geocodeGeo.county;
    if (!result.state_province && geocodeGeo.state_province) result.state_province = geocodeGeo.state_province;
    if (!result.country && geocodeGeo.country) {
      result.country = geocodeGeo.country;
      result.country_code = geocodeGeo.country_code;
    }
  }

  return result;
}

// =============================================================================
// LAZY BACKFILL
// =============================================================================

// Location ids the lazy backfill already attempted this session. Some rows
// are permanently incomplete — geocoding can't conjure a county for places
// that have none — and without this memory those rows sit at the head of
// every batch forever, starving fixable rows behind them (how the French
// Polynesia yoinks stayed invisible on the geography page).
const attemptedBackfillIds = new Set();

/**
 * Pick which rows a backfill pass should attempt: rows missing `country`
 * first (they are omitted from the geography page entirely), then missing
 * state, then county-only; rows already attempted this session go to the
 * back of the line (a fresh round starts once everything has been tried).
 * Pure — exported for tests.
 * @param {Array} needsBackfill - Locations missing some geo field
 * @param {Set} attempted - Location ids already attempted this session
 * @param {number} batchSize - Max rows to return
 * @returns {Array} The rows to attempt now
 */
export function planBackfillBatch(needsBackfill, attempted, batchSize = MAX_BACKFILL_BATCH) {
  const severity = (loc) => (!loc.country ? 0 : !loc.state_province ? 1 : 2);
  let queue = needsBackfill.filter((loc) => !attempted.has(loc.id));
  if (queue.length === 0 && needsBackfill.length > 0) {
    attempted.clear();
    queue = [...needsBackfill];
  }
  return queue.sort((a, b) => severity(a) - severity(b)).slice(0, batchSize);
}

/**
 * Backfill missing geo data for a list of locations
 * Processes locations that are missing any of county/state_province/country
 * @param {Array} locations - Array of location objects with id, lat, lng
 * @returns {Promise<number>} Number of locations updated
 */
export async function backfillMissingGeoData(locations) {
  // Filter to locations that need backfill (have lat/lng but missing any geo field).
  // We backfill even if some fields are present — e.g. national-park yoinks where
  // county/country were captured but state_province ended up null.
  const needsBackfill = locations.filter(loc =>
    loc.lat && loc.lng &&
    (!loc.county || !loc.state_province || !loc.country)
  );

  if (needsBackfill.length === 0) {
    return 0;
  }

  // Limit batch size to avoid hammering the API
  const batch = planBackfillBatch(needsBackfill, attemptedBackfillIds);
  batch.forEach((loc) => attemptedBackfillIds.add(loc.id));
  let updatedCount = 0;

  // Process in parallel (limited batch)
  const updates = await Promise.all(
    batch.map(async (location) => {
      try {
        const geoData = await getLocationGeo({
          placeId: location.google_place_id,
          lat: location.lat,
          lng: location.lng
        });

        // Build a patch that only fills in fields the location is missing AND that
        // we found a value for. Never overwrite an existing value with null.
        const patch = {};
        if (!location.county && geoData.county) patch.county = geoData.county;
        if (!location.state_province && geoData.state_province) patch.state_province = geoData.state_province;
        if (!location.country && geoData.country) patch.country = geoData.country;
        if (!location.country_code && geoData.country_code) patch.country_code = geoData.country_code;

        if (Object.keys(patch).length > 0) {
          const { error } = await gateway.rawUpdate(TABLES.LOCATIONS, {
            filters: { id: `eq.${location.id}` },
            body: patch
          });

          if (!error) {
            updatedCount++;
            return { ...location, ...patch };
          }
        }
      } catch (err) {
        logger.warn('[LocationService] Backfill error for location', location.id, ':', err.message);
      }
      return null;
    })
  );

  return updatedCount;
}

// =============================================================================
// LOCATION STATS
// =============================================================================

/**
 * Calculate location stats (unique counties/states/countries) from yoinks
 * @param {Array} yoinks - Array of yoink objects with locations data
 * @returns {{
 *   counties: Set<string>,
 *   states: Set<string>,  // US states only — the count beside the US map

 *   countries: Set<string>,
 *   byCountry: Map<string, {states: Map<string, {counties: Set<string>, yoinkCount: number}>, yoinkCount: number}>,
 *   locationsNeedingBackfill: Array
 * }}
 */
export function calculateLocationStats(yoinks) {
  const stats = {
    counties: new Set(),
    states: new Set(),
    countries: new Set(),
    byCountry: new Map(),
    locationsNeedingBackfill: []
  };

  // Rows store region names in whatever accent/language form Google used
  // that day, so "Bolívar" and "Bolivar" arrive as different strings for
  // the same state. Group by folded key; display the accented spelling
  // whenever any row carries it (the accented form is Google's canonical
  // local name — the bare-ASCII one is the transliteration).
  const displayNameByKey = new Map();
  const diacritics = (name) => name.normalize('NFD').length - name.length;
  for (const yoink of yoinks) {
    for (const name of [yoink.locations?.country, yoink.locations?.state_province, yoink.locations?.county]) {
      if (!name) continue;
      const key = foldRegionName(name);
      const current = displayNameByKey.get(key);
      if (current === undefined || diacritics(name) > diacritics(current)) {
        displayNameByKey.set(key, name);
      }
    }
  }
  const canonical = (name) => (name ? displayNameByKey.get(foldRegionName(name)) : name);

  // Track which locations we've already processed
  const processedLocationIds = new Set();

  for (const yoink of yoinks) {
    const loc = yoink.locations;
    if (!loc) continue;

    // Skip if we already processed this location
    if (processedLocationIds.has(loc.id)) continue;
    processedLocationIds.add(loc.id);

    // Check if this location needs backfill (missing any of county/state/country).
    // Still count whatever fields are present — we just want the missing ones filled
    // in on a future load so the geography map highlights correctly.
    if (loc.lat && loc.lng && (!loc.county || !loc.state_province || !loc.country)) {
      stats.locationsNeedingBackfill.push({
        id: loc.id,
        lat: loc.lat,
        lng: loc.lng,
        google_place_id: loc.google_place_id,
        county: loc.county,
        state_province: loc.state_province,
        country: loc.country,
        country_code: loc.country_code
      });
      // If everything is missing, skip stats entirely; otherwise fall through and
      // count the partial data we do have.
      if (!loc.county && !loc.state_province && !loc.country) continue;
    }

    const country = canonical(loc.country);
    const stateProvince = canonical(loc.state_province);
    const county = canonical(loc.county);

    // Add to flat sets. The states count is US-only (2026-07-24): the
    // page's map is of US states, and the State Collector badge tops out
    // at all 50 actual US states — provinces elsewhere don't count. Same
    // rule as the awards counter, so badge and header always agree.
    // Non-US states still appear in byCountry (drill-down/world map).
    if (county) stats.counties.add(county);
    if (stateProvince && isUnitedStates(loc)) stats.states.add(stateProvince);
    if (country) stats.countries.add(country);

    // Build hierarchical structure
    if (country) {
      if (!stats.byCountry.has(country)) {
        stats.byCountry.set(country, {
          countryCode: loc.country_code,
          states: new Map(),
          yoinkCount: 0
        });
      }
      const countryData = stats.byCountry.get(country);
      countryData.yoinkCount++;

      // Update countryCode if we find one and it was previously missing
      if (!countryData.countryCode && loc.country_code) {
        countryData.countryCode = loc.country_code;
      }

      if (stateProvince) {
        if (!countryData.states.has(stateProvince)) {
          countryData.states.set(stateProvince, {
            counties: new Set(),
            yoinkCount: 0
          });
        }
        const stateData = countryData.states.get(stateProvince);
        stateData.yoinkCount++;

        if (county) {
          stateData.counties.add(county);
        }
      }
    }
  }

  return stats;
}

/**
 * Format location stats for display
 * @param {object} stats - Stats object from calculateLocationStats
 * @returns {string} Formatted string like "3 counties · 2 states · 1 country"
 */
export function formatLocationStats(stats) {
  const parts = [];

  const countyCount = stats.counties.size;
  const stateCount = stats.states.size;
  const countryCount = stats.countries.size;

  if (countyCount > 0) {
    parts.push(`${countyCount} ${countyCount === 1 ? 'county' : 'counties'}`);
  }
  if (stateCount > 0) {
    parts.push(`${stateCount} US state${stateCount === 1 ? '' : 's'}`);
  }
  if (countryCount > 0) {
    parts.push(`${countryCount} ${countryCount === 1 ? 'country' : 'countries'}`);
  }

  return parts.join(' · ');
}

/**
 * Build nested list data structure for expandable view
 * @param {object} stats - Stats object from calculateLocationStats
 * @returns {Array} Nested array structure for rendering
 */
export function buildNestedLocationList(stats) {
  const list = [];

  // Sort countries by yoink count (descending)
  const sortedCountries = Array.from(stats.byCountry.entries())
    .sort((a, b) => b[1].yoinkCount - a[1].yoinkCount);

  for (const [countryName, countryData] of sortedCountries) {
    const countryItem = {
      name: countryName,
      code: countryData.countryCode,
      yoinkCount: countryData.yoinkCount,
      stateCount: countryData.states.size,
      states: []
    };

    // Sort states by yoink count (descending)
    const sortedStates = Array.from(countryData.states.entries())
      .sort((a, b) => b[1].yoinkCount - a[1].yoinkCount);

    for (const [stateName, stateData] of sortedStates) {
      const stateItem = {
        name: stateName,
        yoinkCount: stateData.yoinkCount,
        countyCount: stateData.counties.size,
        counties: Array.from(stateData.counties).sort()
      };
      countryItem.states.push(stateItem);
    }

    list.push(countryItem);
  }

  return list;
}
